import React, { useEffect, useState } from "react";
import {
  Waypoint,
  EArmType,
  EAxisType,
  toCartesianWaypoint,
  toCylindricalWaypoint,
  createNewCartesianWaypoint,
  createNewCylindricalWaypoint,
  ModuleWaypoint,
} from "../../../Model/Waypoint";
import MtiButton from "../../Buttons/MtiButton";
import Modal from "../../Common/Modal";
import TabView from "./TabView";
import "./WaypointManagerDialog.css";
import TextBox from "../../Common/TextBox";
import { ECarrier } from "../../../Model/ECarrier";
import CalibrationApi from "../../../api/CalibrationApi";
import { useTypedSelector } from "../../../redux/reducers/RootReducer";
import { getModuleStateById } from "../../../redux/reducers/ModuleStateReducer";
import CommonDropDown from "../../Common/DropDown/CommonDropDown";

type Props = {
  show: boolean;
  onHide: () => void;
  hotelNumber: number;
  slotNumber: number;
  onWaypointUpdated: () => void;
  moduleIdentifier: string;
  armTypes: EArmType[];
  carriers: ECarrier[];
  selectedArmType: EArmType;
  selectedCarrier: ECarrier;
  waypoints: Waypoint[];
  allWaypointNames: string[];
};

type DialogState = "MAIN" | "EDIT" | "ADD";

export default function WaypointManagerDialog({
  show,
  onHide,
  hotelNumber,
  slotNumber,
  onWaypointUpdated,
  moduleIdentifier,
  armTypes,
  carriers,
  selectedArmType,
  selectedCarrier,
  waypoints,
  allWaypointNames,
}: Props) {
  const [dialogState, setDialogState] = useState("MAIN" as DialogState);
  const [selectedWaypoint, setSelectedWaypoint] = useState({} as Waypoint);

  const isCartesian = waypoints[0]?.axisType === EAxisType.Cartesian;

  // form edit / add new
  const [armType, setArmType] = useState(selectedArmType);
  const [carrier, setCarrier] = useState(selectedCarrier);
  const [waypointName, setWaypointName] = useState("");

  const [customWaypoints, setCustomWaypoints] = useState(
    waypoints.filter((w) => !w.predefined)
  );
  const [predefinedWaypoints, setPredefinedWaypoints] = useState(
    waypoints.filter((w) => w.predefined)
  );

  const [needResetTabState, SetNeedResetTabState] = useState(false);

  const moduleState = useTypedSelector(
    (state) => getModuleStateById(state)[moduleIdentifier]
  );

  useEffect(() => {
    setCarrier(selectedCarrier);
  }, [carriers]);

  useEffect(() => {
    setArmType(selectedArmType);
  }, [armTypes]);

  useEffect(() => {
    setCustomWaypoints(waypoints.filter((w) => !w.predefined));
    setPredefinedWaypoints(waypoints.filter((w) => w.predefined));
  }, [waypoints]);

  let waypointRowCustom = customWaypoints.map((wp) => {
    return (
      <tr key={"wp-manager-custom-" + wp.name}>
        <td>{wp.name}</td>
        <td>{EArmType[wp.armType]}</td>
        <td>{ECarrier[wp.carrier]}</td>
        <td>
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "center",
              columnGap: 12,
            }}
          >
            <MtiButton
              variant="secondary"
              colorVariant="alternate"
              size="medium"
              className="wp-manager-table-button"
              onClick={() => onClickEdit(wp)}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 13 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.5 0.25L12.75 2.5L4.5 10.75L2.25 8.5L10.5 0.25ZM1.5 9.25L3.75 11.5L0 13L1.5 9.25Z"
                  fill="#0059CC"
                />
              </svg>
            </MtiButton>
            <MtiButton
              variant="secondary"
              colorVariant="alternate"
              size="medium"
              className="wp-manager-table-button"
              onClick={() => onClickDuplicate(wp)}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.75 10.75H0.25V0.25H10.75V1.75H1.75V10.75Z"
                  fill="#0059CC"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M13.75 3.25H3.25V13.75H13.75V3.25ZM12.25 4.75H4.75V12.25H12.25V4.75Z"
                  fill="#0059CC"
                />
              </svg>
            </MtiButton>
            <MtiButton
              variant="secondary"
              colorVariant="error"
              size="medium"
              className="wp-manager-table-button"
              onClick={() => onClickDelete(wp)}
            >
              <svg
                width="12"
                height="16"
                viewBox="0 0 12 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0 3.5V2H3.75V0.5H8.25V2H12V3.5H0ZM4.5 2V1.25H7.5V2H4.5Z"
                  fill="#DB0000"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M11.25 5H0.75V15.5H11.25V5ZM3.75 6.5H2.25V14H3.75V6.5ZM6.75 6.5H5.25V14H6.75V6.5ZM8.25 6.5H9.75V14H8.25V6.5Z"
                  fill="#DB0000"
                />
              </svg>
            </MtiButton>
          </div>
        </td>
      </tr>
    );
  });

  let waypointRowPredefined = predefinedWaypoints.map((wp) => {
    return (
      <tr key={"wp-manager-predefined-" + wp.name}>
        <td>{wp.name}</td>
        <td>{EArmType[wp.armType]}</td>
        <td>{ECarrier[wp.carrier]}</td>
        <td>
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "center",
              columnGap: 12,
            }}
          >
            <MtiButton
              variant="secondary"
              colorVariant="alternate"
              size="medium"
              className="wp-manager-table-button"
              onClick={() => onClickDuplicate(wp)}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.75 10.75H0.25V0.25H10.75V1.75H1.75V10.75Z"
                  fill="#0059CC"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M13.75 3.25H3.25V13.75H13.75V3.25ZM12.25 4.75H4.75V12.25H12.25V4.75Z"
                  fill="#0059CC"
                />
              </svg>
            </MtiButton>
          </div>
        </td>
      </tr>
    );
  });

  const onClickAddNewVector = () => {
    setArmType(selectedArmType);
    setCarrier(selectedCarrier);
    setDialogState("ADD");
  };

  const onClickEdit = (waypoint: Waypoint) => {
    setSelectedWaypoint(waypoint);
    setWaypointName(waypoint.name);
    setArmType(waypoint.armType);
    setCarrier(waypoint.carrier);
    setDialogState("EDIT");
    onWaypointUpdated();
  };

  const onClickDelete = (waypoint: Waypoint) => {
    CalibrationApi.deleteWaypoint(moduleIdentifier, waypoint.name).then(
      (result) => {
        setCustomWaypoints(
          customWaypoints.filter((w) => w.name != waypoint.name)
        );
        onWaypointUpdated();
      }
    );
  };

  const onClickCancel = () => {
    setDialogState("MAIN");
    setWaypointName("");
  };

  const onClickSave = () => {
    let moduleWaypoint: ModuleWaypoint = {
      moduleIdentifier: moduleIdentifier,
    };

    if (isCartesian) {
      const defaultVelocity = moduleState?.imager?.defaultVelocity ??
        moduleState?.psr?.cartesianDefaultVelocity ?? { x: 0, y: 0, z: 0 };
      moduleWaypoint.cartesianWaypoints = [
        createNewCartesianWaypoint(
          waypointName,
          armType,
          carrier,
          defaultVelocity
        ),
      ];
    } else {
      const defaultVelocity = moduleState?.psr?.cylindricalDefaultVelocity ?? {
        t: 0,
        e: 0,
        z: 0,
      };
      moduleWaypoint.cylindricalWaypoints = [
        createNewCylindricalWaypoint(
          waypointName,
          armType,
          carrier,
          defaultVelocity
        ),
      ];
    }

    CalibrationApi.createNewWaypoint(moduleWaypoint).then((result) => {
      setDialogState("MAIN");
      setWaypointName("");
      onWaypointUpdated();
    });
  };

  const onClickUpdate = () => {
    const newWaypoint = {
      ...selectedWaypoint,
      armType: armType,
      carrier: carrier,
    };

    CalibrationApi.updateWaypoint(carrier, hotelNumber, slotNumber, {
      cartesianWaypoints: isCartesian
        ? [toCartesianWaypoint(newWaypoint)]
        : undefined,
      cylindricalWaypoints: !isCartesian
        ? [toCylindricalWaypoint(newWaypoint)]
        : undefined,
      moduleIdentifier: moduleIdentifier,
    }).then((result) => {
      setDialogState("MAIN");
      setWaypointName("");
      onWaypointUpdated();
    });
  };

  const onClickDuplicate = (wp: Waypoint) => {
    const duplicateWp: Waypoint = { ...wp };

    let namesSplit = duplicateWp.name.split("-");
    let lastNumberIndex = Number.parseInt(namesSplit[namesSplit.length - 1]);

    if (!isNaN(lastNumberIndex)) {
      namesSplit.pop();
    } else {
      lastNumberIndex = 1;
    }

    let newName = namesSplit.join("-");

    if (newName.includes("ScanSlotVector")) {
      newName = newName.replace("ScanSlotVector", "Scan-Slot-Vector");
    }

    if (newName.includes("RetrievePlateVector")) {
      newName = newName.replace("RetrievePlateVector", "Retrieve-Plate-Vector");
    }

    while (allWaypointNames.includes(newName + "-" + lastNumberIndex)) {
      lastNumberIndex++;
    }

    newName += "-" + lastNumberIndex;

    duplicateWp.name = newName;
    duplicateWp.predefined = false;
    duplicateWp.supportAutoCalibration = false;

    CalibrationApi.createNewWaypoint({
      moduleIdentifier: moduleIdentifier,
      cartesianWaypoints: isCartesian
        ? [toCartesianWaypoint(duplicateWp)]
        : undefined,
      cylindricalWaypoints: !isCartesian
        ? [toCylindricalWaypoint(duplicateWp)]
        : undefined,
    }).then((result) => {
      onWaypointUpdated();
      SetNeedResetTabState(true);
    });
  };

  const onClickBack = () => {
    setWaypointName("");
    setArmType(armTypes[0]);
    setCarrier(carriers[0]);
    setDialogState("MAIN");
  };

  const onClickHide = () => {
    setDialogState("MAIN");
    SetNeedResetTabState(true);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={onClickHide}
      style={{ width: dialogState === "MAIN" ? 686 : 460, padding: 24 }}
    >
      <Modal.Header
        title={"Manage Vectors"}
        hideButtonEnabled={true}
        className="wp-manager-header-modal"
      />
      {dialogState === "MAIN" && (
        <Modal.Body>
          <TabView
            defaultSelectedIndex={0}
            needResetActveTab={needResetTabState}
            setNeedResetActiveTab={SetNeedResetTabState}
          >
            {{
              Custom: (
                <div className="wp-manager-body-modal">
                  <div className="table-title">
                    <div className="title">List of custom vectors</div>
                    <MtiButton
                      onClick={onClickAddNewVector}
                      variant="secondary"
                      colorVariant="alternate"
                      size="small"
                      style={{
                        minHeight: 24,
                        paddingLeft: 21,
                        paddingRight: 21,
                      }}
                    >
                      ADD NEW VECTOR
                    </MtiButton>
                  </div>
                  <table className="wp-manager-table">
                    <thead>
                      <tr>
                        <th>Vector Name</th>
                        <th>Arm Type</th>
                        <th>Carrier Type</th>
                        <th style={{ width: 136 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>{waypointRowCustom}</tbody>
                  </table>
                </div>
              ),
              Predefined: (
                <div className="wp-manager-body-modal">
                  <div className="table-title">
                    <div className="title">List of predefined vectors</div>
                  </div>
                  <table className="wp-manager-table">
                    <thead>
                      <tr>
                        <th>Vector Name</th>
                        <th>Arm Type</th>
                        <th>Carrier Type</th>
                        <th style={{ width: 136 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>{waypointRowPredefined}</tbody>
                  </table>
                </div>
              ),
            }}
          </TabView>
        </Modal.Body>
      )}
      {dialogState !== "MAIN" && (
        <Modal.Body>
          <div className="wp-manager-editor-header">
            <svg
              onClick={onClickBack}
              className="back-button"
              width="24"
              height="20"
              viewBox="0 0 24 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 8L8 0L11 3L6 8H24V12H6L11 17L8 20L0 12V8Z"
                fill="#414042"
              />
            </svg>
            {dialogState === "EDIT" ? "Edit vector" : "Add new vector"}
          </div>
          <div className="wp-manager-form">
            <div className="wp-manager-item">
              <label className="wp-manager-item-label">Vector Name</label>
              <TextBox
                name="wp-manager-name"
                className="wp-manager-item-input"
                disabled={dialogState === "EDIT"}
                value={waypointName}
                variant="text"
                onChange={(value) => setWaypointName(value as string)}
              />
            </div>
            <div className="wp-manager-item">
              <label className="wp-manager-item-label">Select Arm</label>
              <CommonDropDown
                id="wp-manager-armType-selector"
                disabled={dialogState === "ADD"}
                value={armType}
                onChange={(value) => setArmType(value)}
                dropDownDisplayValues={armTypes.map((armType) => ({
                  display: EArmType[armType],
                  value: armType,
                  disabled: false,
                }))}
                style={{
                  width: "100%",
                  height: 32,
                }}
              />
            </div>
            <div className="wp-manager-item">
              <label className="wp-manager-item-label">Carrier</label>
              <CommonDropDown
                id="wp-manager-carrier-selector"
                disabled={dialogState === "ADD"}
                value={carrier}
                onChange={(value) => setCarrier(value)}
                dropDownDisplayValues={carriers.map((carrier) => ({
                  display: ECarrier[carrier],
                  value: carrier,
                  disabled: false,
                }))}
                style={{
                  width: "100%",
                  height: 32,
                }}
              />
            </div>
          </div>
        </Modal.Body>
      )}
      {dialogState === "MAIN" && (
        <Modal.Footer className="wp-manager-footer-modal">
          <MtiButton
            style={{ width: 120 }}
            onClick={() => onClickHide()}
            size="large"
          >
            DONE
          </MtiButton>
        </Modal.Footer>
      )}
      {dialogState !== "MAIN" && (
        <Modal.Footer className="wp-manager-footer-modal">
          <MtiButton
            style={{ width: 120, marginRight: 10 }}
            onClick={() => onClickCancel()}
            size="large"
            variant="secondary"
          >
            CANCEL
          </MtiButton>
          <MtiButton
            style={{ width: 120 }}
            onClick={() =>
              dialogState === "ADD" ? onClickSave() : onClickUpdate()
            }
            size="large"
          >
            SAVE
          </MtiButton>
        </Modal.Footer>
      )}
    </Modal>
  );
}
